import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { commissions } from "@/api/commissions";
import { useActiveAccount } from "@/components/ActiveAccount";
import { downloadCsv } from "@/lib/csv";
import {
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
} from "@/components/ui";

/**
 * Per-account commission report. Shows the grand total, a per-symbol
 * breakdown, and a daily-by-symbol table. CSV export uses the daily
 * rows (the most useful slice for accounting reconciliation).
 */
export function CommissionsPage() {
  const { active } = useActiveAccount();
  const [filters, setFilters] = useState({ from_ts: "", to_ts: "" });
  const q = useQuery({
    queryKey: ["commissions", active?.id, filters],
    queryFn: () =>
      commissions.report(active!.id, filters.from_ts || undefined, filters.to_ts || undefined),
    enabled: !!active,
    refetchInterval: 60_000,
  });

  if (!active) {
    return <EmptyState title="No active account" description="Pick a trading account to view commissions." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Commissions</h1>
        <div className="flex items-center gap-3">
          <span className="text-2xs uppercase tracking-wider text-term-mute">Total</span>
          <span className="tnum font-mono text-base text-term-fg">
            {q.data ? q.data.grand_total : "—"}
          </span>
          <Button
            variant="ghost"
            disabled={!q.data || q.data.by_day.length === 0}
            onClick={() => {
              if (!q.data) return;
              downloadCsv(
                q.data.by_day as unknown as Record<string, unknown>[],
                `commissions-${new Date().toISOString().slice(0, 10)}`,
              );
            }}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label>From</Label>
              <Input
                type="datetime-local"
                value={filters.from_ts}
                onChange={(e) => setFilters((f) => ({ ...f, from_ts: e.target.value }))}
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="datetime-local"
                value={filters.to_ts}
                onChange={(e) => setFilters((f) => ({ ...f, to_ts: e.target.value }))}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {q.data && Object.keys(q.data.by_symbol).length > 0 && (
        <Card>
          <CardBody>
            <h2 className="mb-2 text-2xs uppercase tracking-wider text-term-mute">By symbol</h2>
            <table className="w-full text-xs">
              <thead className="text-term-mute">
                <tr>
                  <th className="py-1 text-left text-2xs uppercase tracking-wider">Symbol</th>
                  <th className="py-1 text-right text-2xs uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(q.data.by_symbol).map(([sym, tot]) => (
                  <tr key={sym} className="border-t border-term-border">
                    <td className="py-1 font-mono text-term-fg">{sym}</td>
                    <td className="py-1 text-right tnum text-term-fg">{tot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="p-0">
          {q.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !q.data || q.data.by_day.length === 0 ? (
            <EmptyState
              title="No commissions in this window"
              description="Try widening the date range or pick an account with recent activity."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Date</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Fills</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {q.data.by_day.map((d, i) => (
                  <tr key={`${d.date}-${d.symbol}-${i}`} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 tnum text-term-dim">{d.date}</td>
                    <td className="px-3 py-1.5 font-mono text-term-fg">{d.symbol}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{d.fill_count}</td>
                    <td className="px-3 py-1.5 text-right tnum text-term-fg">{d.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
